import React, { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { submissionService } from "@/services/submissionService";
import { userService } from "@/services/userService";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import Pagination from '../Pagination';
import { useSubmissions } from "@/hooks/queries/useSubmissions";
import { useAllAdvisors } from "@/hooks/queries/useAllAdvisors";
import { useQueryClient } from "@tanstack/react-query";
import useAuth from "@/hooks/stores/useAuth";
import CustomDateRangeModal from "./CustomDateRangeModal";
import TableTop from "./TableTop";

// Add this helper function near the top of the file, after the imports
const getDisplayFileName = (submissionName) => {
  if (!submissionName) return "";
  // If the name contains a URL path, get just the filename
  const fullFileName = submissionName.includes('/') ? 
    submissionName.split('/').pop() : 
    submissionName;
  // Remove timestamp prefix if it exists (pattern: numbers followed by underscore)
  return fullFileName.replace(/^\d+_/, '');
};

export const Submissions = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);
  const isInitialRender = useRef(true);
  const { userRole, userId } = useAuth();
  
  // State for search and filters
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedAdvisor, setSelectedAdvisor] = useState(location.state?.selectedAdvisorId || "");
  const [dateFilter, setDateFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useState({});
  const [customDateRange, setCustomDateRange] = useState({
    start: "",
    end: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [legalAdvisors, setLegalAdvisors] = useState([]);
  const [isLoadingAdvisors, setIsLoadingAdvisors] = useState(false);

  // Use React Query for submissions
  const { 
    data: submissionsResponse, 
    isLoading, 
    isError, 
    error,
    isFetching 
  } = useSubmissions(currentPage, searchParams);

  // Fetch all legal advisors for Admin role
  const { data: allAdvisors, isLoading: isLoadingAllAdvisors } = useAllAdvisors();

  // Extract submissions and pagination from the response
  const submissions = submissionsResponse?.data || [];
  const paginationData = submissionsResponse?.pagination || {
    current_page: 1,
    total: 0,
    per_page: 10,
    last_page: 1
  };

  // Fetch legal advisors
  useEffect(() => {
    const fetchLegalAdvisors = async () => {
      try {
        setIsLoadingAdvisors(true);
        
        // For Legal Advisor role, get their own info instead of all advisors
        if (userRole?.toLowerCase() === "legal advisor") {
          if (userId) {
            // Get the current advisor's own data
            const userData = await userService.getUser(userId);
            
            // Set the legal advisor to just the current user
            if (userData && userData.user) {
              setLegalAdvisors([userData.user]);
              
              // For convenience, automatically set the selected advisor
              if (selectedAdvisor === "" && userData.user.id) {
                setSelectedAdvisor(userData.user.id.toString());
                
                // Also update search params
                const newParams = { ...searchParams };
                newParams.advisor_id = userData.user.id.toString();
                setSearchParams(newParams);
              }
            } else {
              setLegalAdvisors([]);
            }
          } else {
            setLegalAdvisors([]);
          }
        }
        // For HOD role, filter advisors assigned to this HOD
        else if (userRole?.toLowerCase() === "head of department") {
          const data = await userService.getlegalAdvisors();
          
          // Handle empty or invalid response
          if (!data || data === "" || (typeof data === 'object' && Object.keys(data).length === 0)) {
            setLegalAdvisors([]);
            return;
          }
          
          // Check proper data structure before filtering
          let advisorsList = Array.isArray(data?.data) ? data.data : 
                            (data?.data?.users ? data.data.users : []);
          
          const advisorsForHOD = advisorsList.filter(advisor => 
            advisor.assigned_to?.id === parseInt(userId));
          
          setLegalAdvisors(advisorsForHOD || []);
        }
        // For Admin role, use the allAdvisors data from the hook
        else {
          if (allAdvisors && allAdvisors.length > 0) {
            // If we already have the data from the useAllAdvisors hook, use it
            setLegalAdvisors(allAdvisors);
            
            // After setting the advisors, prefetch for future visits
            // This ensures the next time the page is loaded, data is already available
            queryClient.prefetchQuery({
              queryKey: ['allAdvisors'],
              queryFn: async () => {
                try {
                  const firstPageResponse = await userService.getlegalAdvisors(1);
                  const pagination = firstPageResponse.pagination || {};
                  const totalPages = pagination.last_page || 1;
                  
                  // Start with first page data
                  let allAdvisorsData = [];
                  if (Array.isArray(firstPageResponse.data)) {
                    allAdvisorsData = [...firstPageResponse.data];
                  } else if (firstPageResponse.data?.users && Array.isArray(firstPageResponse.data.users)) {
                    allAdvisorsData = [...firstPageResponse.data.users];
                  }
                  
                  // If there are more pages, fetch them in parallel
                  if (totalPages > 1) {
                    const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
                    
                    const remainingPagesData = await Promise.all(
                      remainingPages.map(page => userService.getlegalAdvisors(page))
                    );
                    
                    // Process and add advisors from each page
                    remainingPagesData.forEach(pageResponse => {
                      if (Array.isArray(pageResponse.data)) {
                        allAdvisorsData = [...allAdvisorsData, ...pageResponse.data];
                      } else if (pageResponse.data?.users && Array.isArray(pageResponse.data.users)) {
                        allAdvisorsData = [...allAdvisorsData, ...pageResponse.data.users];
                      }
                    });
                  }
                  
                  return allAdvisorsData;
                } catch (error) {
                  console.error('Error prefetching all advisors:', error);
                  return [];
                }
              },
              staleTime: 5 * 60 * 1000, // 5 minutes
            });
          } else {
            // Fallback to regular fetch if hook data is not available yet
            const data = await userService.getlegalAdvisors();
            
            // Handle empty or invalid response
            if (!data || data === "" || (typeof data === 'object' && Object.keys(data).length === 0)) {
              setLegalAdvisors([]);
              return;
            }
            
            // Check different possible data structures
            if (Array.isArray(data?.data)) {
              setLegalAdvisors(data.data);
            } else if (data?.data?.users && Array.isArray(data.data.users)) {
              setLegalAdvisors(data.data.users);
            } else {
              setLegalAdvisors([]);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching legal advisors:", err);
        setLegalAdvisors([]);
        toast.error(err.message || "Failed to fetch legal advisors");
      } finally {
        // Only clear loading state if we're not still loading all advisors (for admin)
        if (userRole?.toLowerCase() !== "admin" || !isLoadingAllAdvisors) {
          setIsLoadingAdvisors(false);
        }
      }
    };

    fetchLegalAdvisors();
  }, [userRole, userId, allAdvisors, queryClient]);
  
  // Update isLoadingAdvisors to consider both initial loading and loading all advisors
  useEffect(() => {
    if (userRole?.toLowerCase() === "admin") {
      setIsLoadingAdvisors(isLoadingAllAdvisors);
    }
  }, [isLoadingAllAdvisors, userRole]);

  // Handle input changes with debouncing for empty input
  const handleSearchInputChange = (value) => {
    setSearchTerm(value);
    
    // We'll debounce the empty string check to avoid immediate API calls on backspace
    if (value === '') {
      // Set a small timeout to debounce clearing
      setTimeout(() => {
        setDebouncedSearchTerm('');
      }, 300);
    } else {
      setDebouncedSearchTerm(value);
    }
  };
  
  // Effect to auto-clear search when input becomes empty
  useEffect(() => {
    if (debouncedSearchTerm === '' && Object.keys(searchParams).length > 0) {
      clearSearch();
    }
  }, [debouncedSearchTerm]);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  // Add a function to handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    
    // Create a new searchParams object
    const newParams = { ...searchParams };
    
    // Check if searchTerm contains @ which likely means it's an email
    if (searchTerm) {
      if (searchTerm.includes('@')) {
        newParams.email = searchTerm;
        delete newParams.name;
      } else {
        newParams.name = searchTerm;
        delete newParams.email;
      }
    } else {
      delete newParams.name;
      delete newParams.email;
    }
    
    // Update search params state
    setSearchParams(newParams);
    
    // Reset to first page when searching
    setCurrentPage(1);
    
    // Maintain focus on the search input
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };
  
  // Add a function to clear the search
  const clearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    
    // Create a new params object without name and email
    const newParams = { ...searchParams };
    delete newParams.name;
    delete newParams.email;
    
    setSearchParams(newParams);
    setCurrentPage(1);
    
    // Maintain focus on the search input after clearing
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Keep dateFilter in sync with searchParams
  useEffect(() => {
    // If searchParams.dateFilter is custom_range, set the UI dropdown to "custom"
    if (searchParams.dateFilter === "custom_range") {
      setDateFilter("custom");
      
      // Make sure customDateRange is also in sync with the search params
      if (searchParams.dateFrom || searchParams.dateTo) {
        setCustomDateRange({
          start: searchParams.dateFrom || '',
          end: searchParams.dateTo || ''
        });
      }
    } else if (searchParams.dateFilter) {
      // Otherwise use the actual value
      setDateFilter(searchParams.dateFilter);
    } else {
      // Clear the date filter if there's no dateFilter in searchParams
      setDateFilter("");
    }
  }, [searchParams.dateFilter, searchParams.dateFrom, searchParams.dateTo]);

  // Update Filter Handlers
  const handleAdvisorChange = (value) => {
    setSelectedAdvisor(value);
    
    // Update search params
    const newParams = { ...searchParams };
    
    if (value) {
      // If we have a value, set it in the search params as advisor_id
      newParams.advisor_id = value;
      
      // If we're a Legal Advisor, pre-set our value
      if (userRole?.toLowerCase() === "legal advisor" && legalAdvisors.length === 1) {
        newParams.advisor_id = legalAdvisors[0].id.toString();
      }
    } else {
      // If no value, remove from search params
      delete newParams.advisor_id;
    }
    
    setSearchParams(newParams);
    setCurrentPage(1);
  };
  
  const handleSortFieldChange = (value) => {
    setSortField(value);
    
    // Update search params
    const newParams = { ...searchParams };
    if (value) {
      newParams.sortField = value;
      // Always include sort direction when a sort field is selected
      if (!newParams.sortOrder) {
        newParams.sortOrder = "asc"; // Default to ascending if not already set
        setSortOrder("asc");
      }
    } else {
      delete newParams.sortField;
      delete newParams.sortOrder; // Remove sort order if no sort field
    }
    
    setSearchParams(newParams);
  };
  
  const toggleSortOrder = () => {
    const newOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newOrder);
    
    // Update search params
    const newParams = { ...searchParams };
    newParams.sortOrder = newOrder;
    setSearchParams(newParams);
  };

  const handleDateFilterChange = (value) => {
    // Always update the dateFilter state
    setDateFilter(value);
    
    // For custom date, always show the modal regardless of previous state
    if (value === "custom") {
      // Force the modal to open for "custom" option even if it was already selected
      setShowModal(true);
      
      // Initialize with existing values if available
      if (searchParams.dateFrom || searchParams.dateTo) {
        setCustomDateRange({
          start: searchParams.dateFrom || '',
          end: searchParams.dateTo || ''
        });
      } else {
        // Reset custom date range when opening modal with no previous selection
        setCustomDateRange({
          start: '',
          end: ''
        });
      }
      
      return; // Exit early without updating search params
    }
    
    // For other date filters, update search params
    const newParams = { ...searchParams };
    
    // Clear any existing date params
    delete newParams.dateFrom;
    delete newParams.dateTo;
    delete newParams.dateFilter;
    
    if (value) {
      // For predefined date filters, just pass the value directly
      newParams.dateFilter = value;
    }
    
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  // Track modal state change
  useEffect(() => {
    // console.log("Modal state changed:", showModal);
  }, [showModal]);

  // Reset modal and custom date states when navigating away
  useEffect(() => {
    return () => {
      setShowModal(false);
      setCustomDateRange({
        start: "",
        end: ""
      });
    };
  }, []);
  
  const applyCustomDateRange = () => {
    // console.log("Applying custom date range, closing modal");
    setShowModal(false);
    
    // Only proceed if at least one date is selected
    if (!customDateRange.start && !customDateRange.end) {
      // If no dates selected, reset to default with no filter
      setDateFilter("");
      const newParams = { ...searchParams };
      delete newParams.dateFrom;
      delete newParams.dateTo;
      delete newParams.dateFilter;
      setSearchParams(newParams);
      return;
    }
    
    // Ensure both start and end dates are set
    // If only one is set, use it for both to create a single-day range
    const finalDateRange = {
      start: customDateRange.start || customDateRange.end,
      end: customDateRange.end || customDateRange.start
    };
    
    // Update the custom date range state
    setCustomDateRange(finalDateRange);
    
    // Update search params with custom date range
    const newParams = { ...searchParams };
    
    // Set dateFilter to "custom_range" to match the API expectation
    newParams.dateFilter = "custom_range"; 
    newParams.dateFrom = finalDateRange.start;
    newParams.dateTo = finalDateRange.end;
    
    // Clear existing filter params (just to be safe)
    delete newParams.date_filter;
    delete newParams.start_date;
    delete newParams.end_date;
    
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const cancelCustomDate = () => {
    // console.log("Cancelling custom date selection, closing modal");
    setShowModal(false);
    
    // If we don't already have a custom date range applied, reset the dropdown
    if (searchParams.dateFilter !== "custom_range") {
      setDateFilter(searchParams.dateFilter || "");
    }
  };

  const handleViewForm = (userId, submissionId) => {
    navigate(`/submissions/${userId}/form/${submissionId}`);
  };

  // Create loading indicator component to reuse
  const LoadingIndicator = () => (
    <div className="flex justify-center py-4">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#F16745]"></div>
    </div>
  );

  // Helper function for form submissions array
  const getFormSubmissionsArray = (formSubmissions) => {
    if (Array.isArray(formSubmissions)) {
      return formSubmissions;
    }
    if (typeof formSubmissions === "object") {
      return Object.values(formSubmissions);
    }
    return [];
  };

  // Update search params when any filter or sort changes
  useEffect(() => {
    // Only run this effect after initial render
    if (!isInitialRender.current) {
      // Create a new searchParams object from existing filters
      const newParams = { ...searchParams };
      
      // Always include sort direction when sort field is present
      if (sortField && !newParams.sortOrder) {
        newParams.sortOrder = sortOrder;
      }
      
      // Update search params state
      setSearchParams(newParams);
    } else {
      isInitialRender.current = false;
    }
  }, [sortField]); // Only trigger when sort field changes

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500">{error?.message || "An error occurred while fetching submissions"}</div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[100vw] overflow-x-hidden lg:container px-7 sm:px-9 lg:px-10 py-12 bg-white min-h-screen">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-4">
          <div>
            <h1 className="text-3xl font-bold text-[#F16745] dark:text-[#F16745] mb-2">Submissions</h1>
            <p className="text-base text-body-color dark:text-dark-6">Manage and monitor user submissions</p>
          </div>
        </div>
      </div>

      <TableTop 
        searchTerm={searchTerm}
        setSearchTerm={handleSearchInputChange}
        sortField={sortField}
        setSortField={handleSortFieldChange}
        selectedAdvisor={selectedAdvisor}
        setSelectedAdvisor={handleAdvisorChange}
        dateFilter={dateFilter}
        handleDateFilterChange={handleDateFilterChange}
        sortOrder={sortOrder}
        toggleSortOrder={toggleSortOrder}
        legalAdvisors={legalAdvisors}
        handleSearch={handleSearch}
        clearSearch={clearSearch}
        userRole={userRole}
        isLoadingAdvisors={isLoadingAdvisors}
        setShowModal={setShowModal}
      />

      {/* Desktop Table View */}
      <div className="hidden lg:block bg-white rounded-lg overflow-hidden w-full">
        <div className="overflow-x-auto w-full">
          <table className="w-full table-auto min-w-full">
            <thead className="border-b border-stroke dark:border-dark-3">
              <tr>
                <th className="px-6 py-9 text-left font-medium text-base text-body-color dark:text-dark-6">
                  Name
                </th>
                <th className="px-6 py-9 text-left font-medium text-base text-body-color dark:text-dark-6">
                  Email
                </th>
                <th className="px-6 py-9 text-left font-medium text-base text-body-color dark:text-dark-6">
                  Progress
                </th>
                <th className="px-6 py-9 text-left text-base font-medium text-body-color dark:text-dark-6">
                  Advisor
                </th>
                <th className="px-6 py-9 text-left text-base font-medium text-body-color dark:text-dark-6">
                  Submissions
                </th>
                <th className="px-6 py-9 text-left text-base font-medium text-body-color dark:text-dark-6">
                  Forms
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="py-8 text-center">
                    <LoadingIndicator />
                  </td>
                </tr>
              ) : submissions.length > 0 ? (
                submissions.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-[#F16745]/5"
                  >
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-[#F16745]">
                      {user.name}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-body-color dark:text-dark-6">
                      {user.email}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-body-color dark:text-dark-6">
                      {Math.round(parseFloat(user.progress))}%
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-body-color dark:text-dark-6">
                      {user.referral_name}
                    </td>
                    <td className="px-6 py-3 text-sm text-body-color dark:text-dark-6">
                      <div className="space-y-2 min-h-[60px]">
                        {getFormSubmissionsArray(user.form_submissions).length > 0 ? (
                          getFormSubmissionsArray(user.form_submissions).map(
                            (submission) => (
                              <div key={submission.id} className="p-2 rounded">
                                <div>
                                  <p className="font-medium">
                                    {getDisplayFileName(submission.name)}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    Created: {format(new Date(submission.created_at), "MMM d, yyyy HH:mm")}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    Updated: {format(new Date(submission.updated_at), "MMM d, yyyy HH:mm")}
                                  </p>
                                </div>
                              </div>
                            )
                          )
                        ) : (
                          <div className="p-6">
                            <p className="text-body-color dark:text-dark-6">N/A</p>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-sm text-body-color dark:text-dark-6">
                      {getFormSubmissionsArray(user.form_submissions).length > 0 ? (
                        getFormSubmissionsArray(user.form_submissions).map(
                          (submission) => (
                            <div key={submission.id} className="mb-2">
                              <button
                                onClick={() =>
                                  handleViewForm(user.id, submission.id)
                                }
                                className="px-3 py-1 bg-[#F16745] text-white text-sm rounded hover:bg-opacity-90 transition-colors"
                              >
                                View Form
                              </button>
                            </div>
                          )
                        )
                      ) : (
                        <div className="mb-2 ml-2">
                          <span className="text-body-color dark:text-dark-6">N/A</span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-5 text-center text-sm text-muted-foreground min-h-[60px]"
                  >
                    No submissions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Add Pagination */}
        {submissions.length > 0 && !isLoading && (
          <div className="hidden lg:block">
            <Pagination
              currentPage={paginationData.current_page}
              totalPages={paginationData.last_page}
              onPageChange={handlePageChange}
              itemsPerPage={paginationData.per_page}
              totalItems={paginationData.total}
            />
          </div>
        )}
      </div>

      {/* Tablet and Mobile Card View */}
      <div className="lg:hidden space-y-4 w-full overflow-x-hidden">
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-1">
            {submissions && submissions.length > 0 ? (
              submissions.map((user) => (
                <div key={user.id} className="bg-white p-4 rounded-lg shadow">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-orange-500 font-medium">{user.name}</h3>
                        <p className="text-sm mt-3 text-gray-600">{user.email}</p>
                      </div>
                      <span className="inline-block bg-[#F16745]/10 text-[#F16745] px-2 py-1 rounded-full text-sm">
                        {Math.round(parseFloat(user.progress))}%
                      </span>
                    </div>

                    <div className="text-sm">
                      <p className="text-gray-600">
                        <span className="font-medium">Advisor:</span> {user.referral_name}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p className="font-medium text-sm">Submissions:</p>
                      {getFormSubmissionsArray(user.form_submissions).length > 0 ? (
                        getFormSubmissionsArray(user.form_submissions).map((submission) => (
                          <div key={submission.id} className="bg-gray-50 p-3 rounded">
                            <p className="font-medium text-sm">{getDisplayFileName(submission.name)}</p>
                            <p className="text-xs text-gray-600">
                              Created: {format(new Date(submission.created_at), "MMM d, yyyy HH:mm")}
                            </p>
                            <p className="text-xs text-gray-600 mb-2">
                              Updated: {format(new Date(submission.updated_at), "MMM d, yyyy HH:mm")}
                            </p>
                            <button
                              onClick={() => handleViewForm(user.id, submission.id)}
                              className="w-full px-3 py-1 bg-[#F16745] text-white text-sm rounded hover:bg-[#F16745] transition-colors"
                            >
                              View Form
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="bg-gray-50 p-3 rounded">
                          <p className="text-gray-600 text-center">N/A</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-2 text-center text-gray-500 py-4">
                No submissions found
              </div>
            )}
          </div>
        )}
        
        {/* Add Pagination for mobile/tablet */}
        {submissions.length > 0 && !isLoading && (
          <div className="mt-4">
            <Pagination
              currentPage={paginationData.current_page}
              totalPages={paginationData.last_page}
              onPageChange={handlePageChange}
              itemsPerPage={paginationData.per_page}
              totalItems={paginationData.total}
            />
          </div>
        )}
      </div>

      {/* Custom Date Range Modal */}
      <CustomDateRangeModal
        showModal={showModal}
        customDateRange={customDateRange}
        setCustomDateRange={setCustomDateRange}
        cancelCustomDate={cancelCustomDate}
        applyCustomDateRange={applyCustomDateRange}
      />
    </div>
  );
};